import { JevCallDetail } from "@admin/traces/JevCallDetail";
import { useSessionKey } from "@auth/sessionStore";
import { Button } from "@scribenote/brian-design-system";
import type { ClinicUser } from "@users/userClient";
import { displayNameOf, useClinicUsers } from "@users/userQueries";
import { useState } from "react";

import type { SlotChange } from "./perceptionClient";
import { clockWord, patientWord } from "./words";

/** brian `RosterChange.cause`, the roster step that made a change (`docs/perception.md`, `room_roster_change`). */
const RULE: Record<string, string> = {
  roster_diff:
    "Roster check: who the camera matched, saw arrive, or let go this tick.",
  staff_naming: "Staff naming: the face compared with staff reference photos.",
  visit_question:
    "Visit question: Jev picked the visit from what was said and seen.",
  voice: "A staff voice heard in the room.",
  person: "The clinic's record of this person.",
  another_room: "Another room holds this name.",
  arrived_elsewhere: "Another room saw them arrive.",
  expected_person: "Matched to someone the clinic expects.",
  room_cleared: "The room was confirmed empty.",
  clinic_conflict: "The visit's patient is placed in another room.",
  manual: "Set by hand.",
};

const CHANGE: Record<string, string> = {
  raised: "more sure",
  doubted: "less sure",
  revised: "changed",
  withdrawn: "dropped",
};

interface Labels {
  users: ClinicUser[] | undefined;
  patients: ReadonlyMap<string, string>;
}

/** A role's ref is its status (`room_roster_rules._ROLE`). */
const refWord = (
  field: string,
  ref: string | null,
  status: string | null,
  { users, patients }: Labels,
) => {
  if (field === "role") return status ?? "none";
  if (ref === null) return "none";
  const label =
    field === "name"
      ? displayNameOf(users, ref, "a staff member")
      : patientWord(ref, patients);
  return status ? `${label} (${status})` : label;
};

const whatWord = (row: SlotChange, labels: Labels) => {
  if (row.field === "slot")
    return row.change === "left" ? "left the roster" : "came onto the roster";
  if (row.change === "left") return `${row.field} gone with the slot`;
  const before = refWord(row.field, row.ref_before, row.status_before, labels);
  const after = refWord(row.field, row.ref_after, row.status_after, labels);
  const how = CHANGE[row.change];
  return `${row.field}: ${before} → ${after}${how ? `, ${how}` : ""}`;
};

const HistoryRow = ({ row, labels }: { row: SlotChange; labels: Labels }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="flex flex-col gap-1 border-t border-rule py-1.5 first:border-t-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <time
          dateTime={row.changed_at}
          className="font-mono text-fine-print text-annotation"
        >
          {clockWord(row.changed_at)}
        </time>
        <span className="text-ink-body">{whatWord(row, labels)}</span>
        {row.jev_calls.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide Jev's answer" : "Jev's answer"}
          </Button>
        )}
      </div>
      <p className="text-fine-print text-annotation">
        {RULE[row.cause] ?? row.cause}
        {row.asked_jev &&
          row.jev_calls.length === 0 &&
          " Jev's answer is no longer kept."}
      </p>
      {open && (
        <div className="flex flex-col gap-3">
          {row.jev_calls.map((uuid) => (
            <JevCallDetail key={uuid} callUuid={uuid} active />
          ))}
        </div>
      )}
    </li>
  );
};

/**
 * A slot's roster changes, oldest first, each with the step that made it (BRI-354).
 * A change backed by Jev answers opens them: the pictures, every option's p and the
 * bar the answer had to clear. Names patients, so the panel hides it from RUM.
 */
export const SlotHistory = ({
  history,
  patients,
}: {
  history: readonly SlotChange[];
  patients: ReadonlyMap<string, string>;
}) => {
  const labels = { users: useClinicUsers(useSessionKey()).data, patients };
  return (
    <section aria-label="How Brian got here" className="flex flex-col gap-1">
      <h3 className="text-sm font-semibold text-ink">How Brian got here</h3>
      {history.length === 0 ? (
        <p className="text-sm text-annotation">
          No changes recorded for this slot.
        </p>
      ) : (
        <ol className="flex flex-col text-sm">
          {history.map((row, i) => (
            <HistoryRow
              key={`${row.changed_at}-${i}`}
              row={row}
              labels={labels}
            />
          ))}
        </ol>
      )}
    </section>
  );
};
